
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { 
  CreditCard, 
  Calendar, 
  DollarSign, 
  Settings, 
  ExternalLink 
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { pricingTiers } from "./PricingPlans";

export const SubscriptionManager = () => {
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setSubscription(data);
    } catch (error: any) {
      console.error('Error fetching subscription:', error);
      toast({
        title: "Error",
        description: "Failed to load subscription details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session');
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Error creating portal session:', error);
      toast({
        title: "Error",
        description: "Failed to open subscription management. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpgrade = () => {
    navigate('/plan-selection');
  };

  const handleSelectPlan = async (priceId: string, subscriptionType: string) => {
    try {
      setCheckoutLoading(priceId);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to continue with your purchase.",
          variant: "destructive",
        });
        navigate("/auth?mode=signin");
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          priceId, 
          subscriptionType
        }
      });

      if (error) {
        console.error('Checkout error:', error);
        throw error;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {subscription ? (
          <>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="font-medium capitalize">
                  {subscription.subscription_type} Plan
                </p>
                <p className="text-sm text-gray-600">
                  Status: <Badge variant="default">Active</Badge>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">
                  {subscription.subscription_type === 'lifetime' ? 'Lifetime Access' : 'Monthly Billing'}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleManageSubscription}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Manage Subscription
                <ExternalLink className="h-3 w-3" />
              </Button>
              
              {subscription.subscription_type !== 'lifetime' && (
                <Button
                  onClick={handleUpgrade}
                  className="flex items-center gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  Upgrade Plan
                </Button>
              )}
            </div>

            <div className="pt-4 border-t">
              <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-center"
                  >
                    View All Available Plans
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Choose Your Plan</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-4">
                    {pricingTiers.map((tier) => (
                      <Card key={tier.id} className="relative border-gray-200 h-full flex flex-col">
                        {tier.label && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                            <Badge className="bg-cso-600 text-white whitespace-nowrap">{tier.label}</Badge>
                          </div>
                        )}
                        
                        <CardHeader className="flex-shrink-0">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-xl font-bold">{tier.name}</CardTitle>
                            {tier.isBeta && (
                              <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                                BETA
                              </Badge>
                            )}
                          </div>
                          <div className="text-3xl font-bold">
                            {tier.price}
                            {!tier.isLifetime && (
                              <span className="text-sm font-normal text-gray-600">/month</span>
                            )}
                          </div>
                          <p className="text-gray-600 min-h-[3rem] flex items-center">{tier.description}</p>
                        </CardHeader>
                        
                        <CardContent className="flex-grow flex flex-col">
                          <ul className="space-y-3 flex-grow">
                            {tier.features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0">✓</span>
                                <span className="text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                          
                          <div className="mt-6">
                            <Button
                              className="w-full"
                              onClick={() => handleSelectPlan(tier.id, tier.name.toLowerCase())}
                              disabled={checkoutLoading === tier.id}
                            >
                              {checkoutLoading === tier.id ? "Processing..." : tier.cta}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </>
        ) : (
          <div className="text-center space-y-4">
            <p className="text-gray-600">No active subscription found.</p>
            <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  View Available Plans
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Choose Your Plan</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-4">
                  {pricingTiers.map((tier) => (
                    <Card key={tier.id} className="relative border-gray-200 h-full flex flex-col">
                      {tier.label && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                          <Badge className="bg-cso-600 text-white whitespace-nowrap">{tier.label}</Badge>
                        </div>
                      )}
                      
                      <CardHeader className="flex-shrink-0">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-xl font-bold">{tier.name}</CardTitle>
                          {tier.isBeta && (
                            <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                              BETA
                            </Badge>
                          )}
                        </div>
                        <div className="text-3xl font-bold">
                          {tier.price}
                          {!tier.isLifetime && (
                            <span className="text-sm font-normal text-gray-600">/month</span>
                          )}
                        </div>
                        <p className="text-gray-600 min-h-[3rem] flex items-center">{tier.description}</p>
                      </CardHeader>
                      
                      <CardContent className="flex-grow flex flex-col">
                        <ul className="space-y-3 flex-grow">
                          {tier.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0">✓</span>
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <div className="mt-6">
                          <Button
                            className="w-full"
                            onClick={() => handleSelectPlan(tier.id, tier.name.toLowerCase())}
                            disabled={checkoutLoading === tier.id}
                          >
                            {checkoutLoading === tier.id ? "Processing..." : tier.cta}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
